import CloseIcon from './CloseIcon.jsx';
import { currentKp, observationState } from '../utils/observations.js';
// Right-side panel for Solar mode.
// Empty state → Key Moments timeline derived from CMEs / forecast / reentries.
// Selected state → detail card for a specific CME, reentry, forecast event,
// or one of the four NOAA-scale chips (G/R/S/Flare) from SolarBriefing.

import { useMemo, useState } from 'react';

// ── helpers ────────────────────────────────────────────────────────────────

function hoursFromNow(iso) {
  if (!iso) return null;
  return (new Date(iso) - Date.now()) / 3600000;
}

function fmtUtc(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  const p = n => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} `
       + `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}Z`;
}

export function fmtTMinus(hours) {
  if (hours == null) return '-';
  if (hours === 0) return 'NOW';
  if (hours < 0) return `T+${(-hours).toFixed(1)}h`;
  if (hours < 1) return `T−${Math.round(hours * 60)}m`;
  return `T−${hours.toFixed(1)}h`;
}

export function buildMoments(cmes, reentries, forecast) {
  const items = [];

  for (const c of (cmes || []).filter(c => c.inbound && c.hours_to_arrival != null && c.hours_to_arrival > 0)) {
    items.push({
      id:       `cme:${c.activity_id}`,
      kind:     'cme',
      tone:     c.earth_is_glancing_blow ? '' : 'rose',
      hours:    c.hours_to_arrival,
      title:    'CME shock front arrives',
      meta:     c.earth_is_glancing_blow ? 'glancing blow' : 'direct impact',
      payload:  c,
    });
  }

  if (forecast?.points?.length) {
    const futurePts = forecast.points.filter(p => new Date(p.t).getTime() > Date.now());
    if (futurePts.length) {
      const peak = futurePts.reduce((m, p) => (p.kp > m.kp ? p : m), futurePts[0]);
      const h = hoursFromNow(peak.t);
      if (h != null && h > 0 && peak.kp >= 4) {
        items.push({
          id:      `peak:${peak.t}`,
          kind:    'forecast-peak',
          tone:    peak.kp >= 5 ? 'rose' : '',
          hours:   h,
          title:   `Peak Kp ${peak.kp.toFixed(1)}`,
          meta:    peak.kp >= 5 ? `G${Math.floor(peak.kp - 4)} conditions` : 'unsettled',
          payload: { ...peak, futurePoints: futurePts },
        });
      }
    }
  }

  for (const r of (reentries || []).slice(0, 3)) {
    const h = hoursFromNow(r.decay_epoch);
    if (h != null && h > 0 && h < 72) {
      items.push({
        id:      `reentry:${r.norad_id}`,
        kind:    'reentry',
        tone:    'cyan',
        hours:   h,
        title:   `Reentry: ${r.name}`,
        meta:    r.country || (r.source === 'tip' ? 'TIP refined' : 'decay msg'),
        payload: r,
      });
    }
  }

  if (forecast?.points?.length) {
    const tail = forecast.points.find(p => {
      const h = hoursFromNow(p.t);
      return h > 36 && h < 72 && p.kp <= 3.5;
    });
    if (tail) {
      items.push({
        id:    `recovery:${tail.t}`,
        kind:  'recovery',
        tone:  'dim',
        hours: hoursFromNow(tail.t),
        title: 'Conditions ease',
        meta:  `Kp → ${tail.kp.toFixed(1)}`,
        payload: tail,
      });
    }
  }

  items.sort((a, b) => a.hours - b.hours);
  return items.slice(0, 6);
}

// ── main component ─────────────────────────────────────────────────────────

export default function SolarRightPanel({
  weather, cmes = [], reentries = [], forecast,
  selectedEvent, onSelectEvent, onClose,
}) {
  const moments = useMemo(
    () => buildMoments(cmes, reentries, forecast),
    [cmes, reentries, forecast]
  );

  if (selectedEvent) {
    return <SolarEventCard event={selectedEvent} weather={weather} onClose={onClose} />;
  }

  return (
    <div className="hg-side-panel">
      <div className="hg-side-header">
        <span className="hg-side-title">Key Moments</span>
        <span className="hg-side-meta">{moments.length} upcoming</span>
      </div>

      {moments.length === 0 && (
        <div className="hg-side-empty">
          No upcoming events in the available records.
          <br /><span style={{ color: 'var(--txt-dim)' }}>Missing forecasts do not establish quiet conditions. Check the Solar briefing for available observations.</span>
        </div>
      )}

      <ul className="hg-moments">
        {moments.map(m => (
          <li key={m.id} className={`hg-moment ${m.tone}`}>
            <button className="hg-moment-btn" onClick={() => onSelectEvent?.(m)}>
              <span className={`hg-moment-t ${m.tone}`}>{fmtTMinus(m.hours)}</span>
              <span className="hg-moment-x">
                {m.title}
                <span className="meta"> · {m.meta}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>

      <div className="hg-side-footer">
        <div className="hg-side-footer-l">Tap an event or storm-scale chip</div>
      </div>
    </div>
  );
}

// ── Event detail card ──────────────────────────────────────────────────────

export function SolarEventCard({ event, weather, onClose }) {
  return (
    <div className="hg-side-panel">
      <div className="hg-side-header">
        <span className="hg-side-title">{eventLabel(event.kind)}</span>
        <button className="og-solar-back" onClick={onClose}>Back to key moments</button>
      </div>

      <div className="hg-event-headline">
        <span className={`hg-moment-t ${event.tone}`}>{fmtTMinus(event.hours)}</span>
        <span className="hg-event-title">{event.title}</span>
      </div>

      {event.kind === 'cme'           && <CmeDetail   c={event.payload} weather={weather} />}
      {event.kind === 'forecast-peak' && <PeakDetail  peak={event.payload} />}
      {event.kind === 'reentry'       && <ReentryDetail r={event.payload} />}
      {event.kind === 'recovery'      && <RecoveryDetail p={event.payload} />}

      {event.kind === 'scale-g' && <GeomagDetail    payload={event.payload} weather={weather} />}
      {event.kind === 'scale-r' && <RadioDetail     payload={event.payload} />}
      {event.kind === 'scale-s' && <RadiationDetail payload={event.payload} />}
      {event.kind === 'flare'   && <FlareDetail     payload={event.payload} />}
      {event.kind === 'schumann' && <SchumannDetail payload={event.payload} />}
    </div>
  );
}

function eventLabel(kind) {
  return ({
    cme:             'CME ARRIVAL',
    'forecast-peak': 'FORECAST PEAK',
    reentry:         'REENTRY EVENT',
    recovery:        'CONDITIONS EASE',
    'scale-g':       'GEOMAGNETIC STORM',
    'scale-r':       'RADIO BLACKOUT',
    'scale-s':       'RADIATION STORM',
    flare:           'SOLAR FLARE · X-RAY',
    schumann:        'SCHUMANN RESONANCE',
  })[kind] || 'EVENT';
}

function Row({ k, v, accent }) {
  return (
    <div className="hg-event-row">
      <span className="hg-event-k">{k}</span>
      <span className="hg-event-v" style={accent ? { color: accent } : null}>{v}</span>
    </div>
  );
}

function CmeDetail({ c, weather }) {
  const kpPred = c.kp_90 || c.kp_18 || c.kp_135 || c.kp_180;
  return (
    <div className="hg-event-body">
      <Row k="Source"      v={c.source_location || 'unknown'} />
      <Row k="Speed"       v={c.speed_km_s ? `${c.speed_km_s.toLocaleString()} km/s` : '-'} />
      <Row k="Half-angle"  v={c.half_angle_deg != null ? `${c.half_angle_deg}°` : '-'} />
      <Row k="Earth arrival" v={fmtUtc(c.earth_arrival_time)} />
      <Row k="Impact type" v={c.earth_is_glancing_blow ? 'glancing blow' : 'direct'}
           accent={c.earth_is_glancing_blow ? 'var(--amber)' : 'var(--warn)'} />
      {kpPred != null && (
        <Row k="Predicted Kp" v={`~${kpPred.toFixed(1)}`}
             accent={kpPred >= 5 ? 'var(--warn)' : kpPred >= 4 ? 'var(--amber)' : 'var(--green)'} />
      )}
      {weather?.wind?.bz != null && (
        <Row k="Current Bz" v={`${weather.wind.bz.toFixed(1)} nT`}
             accent={weather.wind.bz < -5 ? 'var(--amber)' : 'var(--green)'} />
      )}
      {c.note && (
        <div className="hg-event-note">
          <div className="hg-event-note-l">DONKI note</div>
          <div className="hg-event-note-x">{c.note}</div>
        </div>
      )}
    </div>
  );
}

function PeakDetail({ peak }) {
  const next = (peak.futurePoints || []).slice(0, 8);
  return (
    <div className="hg-event-body">
      <Row k="Peak time"  v={fmtUtc(peak.t)} />
      <Row k="Kp value"   v={peak.kp.toFixed(1)}
           accent={peak.kp >= 5 ? 'var(--warn)' : 'var(--amber)'} />
      <Row k="Storm level" v={peak.kp >= 5 ? `G${Math.floor(peak.kp - 4)}` : 'unsettled'} />
      <Row k="Source"     v="NOAA SWPC 3-day" />

      {next.length > 0 && (
        <div className="hg-event-note">
          <div className="hg-event-note-l">Next 24 hours</div>
          <div className="hg-event-mini">
            {next.map(p => (
              <div key={p.t} className="hg-event-mini-row">
                <span className="hg-event-mini-t">{new Date(p.t).toUTCString().slice(17, 22)}</span>
                <span className="hg-event-mini-bar">
                  <span className="hg-event-mini-fill"
                        style={{ width: `${Math.min(100, (p.kp / 9) * 100)}%` }} />
                </span>
                <span className="hg-event-mini-v">{p.kp.toFixed(1)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ReentryDetail({ r }) {
  return (
    <div className="hg-event-body">
      <Row k="NORAD"       v={r.norad_id} />
      <Row k="Name"        v={r.name} />
      <Row k="Country"     v={r.country || '-'} />
      <Row k="Decay"       v={fmtUtc(r.decay_epoch)} />
      <Row k="Source"      v={r.source === 'tip' ? 'TIP (refined)' : 'decay message'}
           accent={r.source === 'tip' ? 'var(--amber)' : 'var(--cyan)'} />
      <Row k="Uncertainty" v={r.uncertainty_days != null
                                ? `±${(r.uncertainty_days * 24).toFixed(1)}h` : '-'} />
    </div>
  );
}

function RecoveryDetail({ p }) {
  return (
    <div className="hg-event-body">
      <Row k="When"  v={fmtUtc(p.t)} />
      <Row k="Kp"    v={p.kp.toFixed(1)} accent="var(--green)" />
      <Row k="Note"  v="conditions return to background levels" />
    </div>
  );
}

// ── NOAA scale detail cards ────────────────────────────────────────────────

const G_DESCRIPTIONS = {
  0: 'Magnetosphere calm. No operational impacts. Aurora confined to the high arctic.',
  1: 'Minor storm. Weak power-grid fluctuations possible. Aurora visible at high latitudes (Yukon, northern Scotland).',
  2: 'Moderate storm. Power systems in high-latitude regions may experience voltage alarms. Aurora as low as 55°N.',
  3: 'Strong storm. Voltage corrections required on some power systems. Aurora as low as 50°N. HF radio degraded.',
  4: 'Severe storm. Widespread voltage control problems. Surface charging on satellites. Aurora as low as 45°N.',
  5: 'Extreme storm. Possible widespread voltage control problems and grid system collapse. Aurora visible to tropical latitudes.',
};

function GeomagDetail({ payload, weather }) {
  const c   = payload?.conditions;
  const g   = c?.geomag;
  const kp  = currentKp(weather);
  const lvl = c?.status === 'fresh' ? g?.scale : null;
  const desc = G_DESCRIPTIONS[lvl] || 'Current observations are unavailable.';
  const hist = (weather?.kp?.history || []).slice(-24);

  return (
    <div className="hg-event-body">
      <Row k="Current Kp" v={(kp == null ? '-' : kp.toFixed(1))}
           accent={kp >= 5 ? 'var(--warn)' : kp >= 4 ? 'var(--amber)' : 'var(--green)'} />
      <Row k="NOAA scale" v={`G${lvl ?? '-'}`}
           accent={lvl >= 4 ? 'var(--warn)' : lvl >= 2 ? 'var(--amber)' : 'var(--green)'} />
      <Row k="Label"      v={g?.label || (lvl === 0 ? 'Quiet' : '-')} />
      {weather?.wind?.bz != null && (
        <Row k="IMF Bz"   v={`${weather.wind.bz.toFixed(1)} nT`}
             accent={weather.wind.bz < -5 ? 'var(--amber)' : 'var(--green)'} />
      )}
      {c?.observed_at && <Row k="Observed" v={fmtUtc(c.observed_at)} />}

      <div className="hg-event-note">
        <div className="hg-event-note-l">G{lvl ?? '-'} · what it means</div>
        <div className="hg-event-note-x">{desc}</div>
      </div>

      {hist.length > 0 && (
        <div className="hg-event-note">
          <div className="hg-event-note-l">Kp last 24h</div>
          <div className="hg-event-mini">
            {hist.filter((_, i) => i % 3 === 0).map(p => (
              <div key={p.t} className="hg-event-mini-row">
                <span className="hg-event-mini-t">{new Date(p.t).toUTCString().slice(17, 22)}</span>
                <span className="hg-event-mini-bar">
                  <span className="hg-event-mini-fill"
                        style={{ width: `${Math.min(100, (p.kp / 9) * 100)}%` }} />
                </span>
                <span className="hg-event-mini-v">{p.kp.toFixed(1)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const R_DESCRIPTIONS = {
  0: 'No active blackout. HF propagation normal.',
  1: 'Minor degradation of HF radio communication on sunlit side, occasional loss of radio contact. Low-frequency navigation signals degraded for brief intervals.',
  2: 'Limited blackout of HF radio communication on sunlit side, loss of radio contact for tens of minutes. Degradation of low-frequency navigation signals.',
  3: 'Wide-area blackout of HF radio communication on sunlit side, loss of radio contact for about an hour. Low-frequency navigation signals degraded for about an hour.',
  4: 'HF radio communication blackout on most of the sunlit side of Earth for one to two hours. HF radio contact lost during this time. Navigation at low frequencies degraded for hours.',
  5: 'Complete HF blackout on sunlit side lasting hours. Low-frequency navigation outages cause loss of positioning for hours.',
};

function RadioDetail({ payload }) {
  const c     = payload?.conditions;
  const r     = c?.radio;
  const scale = payload?.conditions?.status === 'fresh' ? r?.scale : null;
  const desc  = R_DESCRIPTIONS[scale] || 'Current observations are unavailable.';
  return (
    <div className="hg-event-body">
      <Row k="NOAA scale" v={`R${scale ?? '-'}`}
           accent={scale >= 4 ? 'var(--warn)' : scale >= 2 ? 'var(--amber)' : 'var(--green)'} />
      <Row k="Label"      v={r?.label || (scale === 0 ? 'None' : '-')} />
      {r?.minor_prob_24h != null && (
        <Row k="Minor 24h prob" v={`${r.minor_prob_24h}%`} />
      )}
      {r?.major_prob_24h != null && (
        <Row k="Major 24h prob" v={`${r.major_prob_24h}%`}
             accent={r.major_prob_24h >= 25 ? 'var(--amber)' : null} />
      )}
      {c?.observed_at && <Row k="Observed" v={fmtUtc(c.observed_at)} />}

      <div className="hg-event-note">
        <div className="hg-event-note-l">R{scale ?? '-'} · what it means</div>
        <div className="hg-event-note-x">{desc}</div>
      </div>

      <div className="hg-event-note">
        <div className="hg-event-note-l">Driver</div>
        <div className="hg-event-note-x">
          Radio blackouts are triggered by X-ray flares from the Sun. The X-rays
          ionize the dayside D-region of the ionosphere, absorbing HF signals
          (3–30 MHz). The blackout follows the flare envelope, typically
          minutes to a few hours.
        </div>
      </div>
    </div>
  );
}

const S_DESCRIPTIONS = {
  0: 'No active radiation storm. Proton flux at background.',
  1: 'Minor storm. Infrequent single-event upsets possible on satellites. No biological hazard.',
  2: 'Moderate storm. Single-event upsets, noise in imaging systems. Slight reduction in polar HF propagation.',
  3: 'Strong storm. Single-event upsets, noise in imaging systems, slight reduction of efficiency in solar panels. Radiation hazard avoidance recommended for astronauts on EVA. Passengers and crew in commercial jets at high latitudes may be exposed to elevated radiation.',
  4: 'Severe storm. Memory device problems, noise on imaging systems, star-tracker problems may cause orientation issues. Astronauts on EVA at risk. High-latitude flights diverted.',
  5: 'Extreme storm. Satellites may be rendered useless, memory impacts may cause loss of control. Aircraft passengers experience significant elevated radiation. Astronauts face unavoidable hazard.',
};

function RadiationDetail({ payload }) {
  const c     = payload?.conditions;
  const s     = c?.radiation;
  const scale = payload?.conditions?.status === 'fresh' ? s?.scale : null;
  const desc  = S_DESCRIPTIONS[scale] || 'Current observations are unavailable.';
  return (
    <div className="hg-event-body">
      <Row k="NOAA scale" v={`S${scale ?? '-'}`}
           accent={scale >= 4 ? 'var(--warn)' : scale >= 2 ? 'var(--amber)' : 'var(--green)'} />
      <Row k="Label"      v={s?.label || (scale === 0 ? 'None' : '-')} />
      {s?.prob_24h != null && (
        <Row k="24h prob"   v={`${s.prob_24h}%`}
             accent={s.prob_24h >= 25 ? 'var(--amber)' : null} />
      )}
      {c?.observed_at && <Row k="Observed" v={fmtUtc(c.observed_at)} />}

      <div className="hg-event-note">
        <div className="hg-event-note-l">S{scale ?? '-'} · what it means</div>
        <div className="hg-event-note-x">{desc}</div>
      </div>

      <div className="hg-event-note">
        <div className="hg-event-note-l">Threshold</div>
        <div className="hg-event-note-x">
          Measured as integral proton flux at ≥10 MeV (GOES). S1 starts at
          10 pfu, with each scale step a 10× increase. Most events arrive
          within 30 minutes of an X-class flare or fast halo CME.
        </div>
      </div>
    </div>
  );
}

const FLARE_LETTER_DESC = {
  A: 'Background level. Below detection threshold for operational impacts.',
  B: 'Minor flux. No operational impacts; common during the rising phase of the solar cycle.',
  C: 'Common flare. Small flares with few noticeable consequences on Earth. Possible HF degradation on sunlit polar paths.',
  M: 'Moderate flare. Can cause brief HF radio blackouts on sunlit side; sometimes followed by minor radiation storms.',
  X: 'Major flare. Long-lasting HF blackouts, possible radiation storms, and CME-driven geomagnetic storms in 18–72 hours.',
};

function FlareDetail({ payload }) {
  const f = payload?.flare;
  if (!f || f.class_letter === '-') {
    return (
      <div className="hg-event-body">
        <div className="hg-side-empty" style={{ padding: '12px 0' }}>
          GOES X-ray data not yet available. The Heliograph polls every 5 minutes;
          a sample will appear shortly.
        </div>
      </div>
    );
  }

  const letter = f.class_letter;
  const desc   = FLARE_LETTER_DESC[letter] || FLARE_LETTER_DESC.A;
  const cls    = `${letter}${f.mantissa != null ? f.mantissa : ''}`;
  const accent = letter === 'X' ? 'var(--warn)'
              : letter === 'M' ? 'var(--amber)'
              : letter === 'C' ? 'var(--cyan)' : 'var(--green)';

  return (
    <div className="hg-event-body">
      <Row k="Class"        v={cls} accent={accent} />
      <Row k="Severity"     v={f.class_label} />
      <Row k="Peak time"    v={fmtUtc(f.peak_time)} />
      <Row k="Peak flux"    v={f.flux != null ? f.flux.toExponential(2) + ' W/m²' : '-'} />
      {f.current_flux != null && (
        <Row k="Current flux" v={f.current_flux.toExponential(2) + ' W/m²'} />
      )}
      <Row k="Channel"      v={f.channel || '-'} />
      <Row k="Source"       v="GOES primary (NOAA SWPC)" />

      <div className="hg-event-note">
        <div className="hg-event-note-l">{letter}-class · what it means</div>
        <div className="hg-event-note-x">{desc}</div>
      </div>

      <div className="hg-event-note">
        <div className="hg-event-note-l">Scale primer</div>
        <div className="hg-event-note-x">
          Flare class is set by GOES soft X-ray flux (0.1–0.8 nm). Each letter
          is a decade: A ≥ 10⁻⁸, B ≥ 10⁻⁷, C ≥ 10⁻⁶, M ≥ 10⁻⁵, X ≥ 10⁻⁴ W/m².
          The mantissa is the linear value within that decade (e.g. M2.4 = 2.4 × 10⁻⁵).
        </div>
      </div>
    </div>
  );
}

// -- Schumann resonance detail ----------------------------------------------

function SchumannDetail({ payload }) {
  const s = payload?.schumann;
  const lastMod = s?.upstreamLastModified;
  const [imageFailed,setImageFailed] = useState(false);
  const imageState = imageFailed ? 'unavailable' : observationState(s?.cacheState === 'stale' ? 'stale':'fresh',lastMod,Date.now(),2*3600000);
  const stale = imageState !== 'fresh';

  // Cache-bust the image so we get a fresh proxy hit each time the card opens
  // (vs. relying on browser cache of the previous /api/schumann fetch).
  // The 5-min API cache still smooths repeat opens within the window.
  const imgSrc = `${import.meta.env.VITE_API_BASE || '/api'}/schumann/spectrogram?ts=${
    encodeURIComponent(s?.fetchedAt || Date.now())
  }`;

  return (
    <div className="hg-event-body">
      <Row k="Source"     v="Cumiana VLF (NW Italy)" accent="var(--cyan)" />
      <Row k="Operator"   v="Renato Romero � IK1QFK" />
      <Row k="Coverage"   v="1�105 Hz � 8-hour window" />
      <Row k="Bands"      v="7.83 / 14.3 / 20.8 / 27.3 / 33.8 Hz" />
      {lastMod && <Row k="Last update" v={new Date(lastMod).toUTCString().slice(5, 22) + ' UTC'} />}
      <Row k="Status" v={imageState === 'fresh' ? 'current source image' : imageState === 'stale' ? 'stale source image' : 'source image unavailable / unconfirmed'}
           accent={stale ? 'var(--amber)' : 'var(--green)'} />

      <img className="hg-schumann-img"
           src={imgSrc}
           alt="Schumann resonance spectrogram from Cumiana VLF station"
           onError={(e) => { setImageFailed(true); e.target.style.display = 'none'; }} />
      <div className="hg-schumann-caption">
        <span>{stale ? 'UNAVAILABLE OR STALE' : 'CURRENT SOURCE IMAGE'}</span>
        <span className="src">vlf.it � ? 5 min</span>
      </div>

      <div className="hg-event-note">
        <div className="hg-event-note-l">What you're looking at</div>
        <div className="hg-event-note-x">
          Schumann resonances are standing electromagnetic waves trapped in the
          Earth-ionosphere cavity. The cavity acts like a global waveguide,
          fed by roughly 50 lightning strikes per second worldwide. The
          brightest horizontal bands sit at 7.83, 14.3, 20.8, 27.3, and 33.8 Hz �
          the resonant modes. Brighter = stronger lightning activity globally;
          vertical streaks are local sferics or interference.
        </div>
      </div>

      <div className="hg-event-note">
        <div className="hg-event-note-l">Why it's here</div>
        <div className="hg-event-note-x">
          Geomagnetic storms can modulate the ionosphere's lower boundary,
          shifting the resonance frequencies slightly. Cross-referencing with
          the Kp index above can reveal whether brightness changes are global
          (storm-driven) or just elevated lightning in a particular region.
        </div>
      </div>
    </div>
  );
}